import React, { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { MailCheck, Loader2 } from "lucide-react";

/**
 * UnverifiedEmailBanner. calm, persistent banner shown to users whose
 * email_verified flag is false. Lives at the top of the Dashboard and Account
 * pages. Offers a one-click "Resend" button that re-issues a fresh token.
 *
 * Hidden automatically for verified users. Returns null for non-authed
 * visitors (no flash).
 */
export default function UnverifiedEmailBanner() {
 const { user } = useAuth();
 const [busy, setBusy] = useState(false);
 // Resend is rate-limited server-side to 5 minutes after registration. The
 // very first click within ~5min of signup will always 429. Surface that as a
 // disabled+countdown state so new users don't get an immediate error toast.
 const [cooldown, setCooldown] = useState(300);

 useEffect(() => {
 if (cooldown <= 0) return;
 const id = setInterval(() => setCooldown(c => Math.max(0, c - 1)), 1000);
 return () => clearInterval(id);
 }, [cooldown]);

 if (!user || user.email_verified !== false) return null;

 const resend = async () => {
 if (cooldown > 0) return;
 setBusy(true);
 try {
 await api.post("/auth/resend-verification");
 toast.success("Verification email sent. check your inbox.", { duration: 6000 });
 setCooldown(300);
 } catch (e) {
 if (e.response?.status === 429) {
 setCooldown(300);
 }
 toast.error(e.response?.data?.detail || "Couldn't send right now. Try again in 5 minutes.");
 } finally {
 setBusy(false);
 }
 };

 const fmtCooldown = (s) => {
 const m = Math.floor(s / 60);
 const r = s % 60;
 return `${m}:${String(r).padStart(2, "0")}`;
 };

 return (
 <div
 className="mb-6 border border-[#C9A646]/40 bg-[#FFF8E7] px-4 py-3 flex items-start gap-3"
 data-testid="unverified-email-banner"
 >
 <div className="w-9 h-9 flex items-center justify-center bg-white border border-[#C9A646]/50 flex-shrink-0 mt-0.5">
 <MailCheck size={15} strokeWidth={1.75} className="text-[#C9A646]" />
 </div>
 <div className="flex-1 min-w-0">
 <div className="font-serif text-[15px] text-[#0D2B52] mb-0.5">Please verify your email</div>
 <p className="text-[13px] text-[#1A1A1A] font-serif leading-snug mb-2">
 We sent a verification link to <strong>{user.email}</strong>. You can browse TenantTrail,
 but uploading documents and asking Compass questions need a verified email.
 </p>
 <button
 onClick={resend}
 disabled={busy || cooldown > 0}
 className="btn-primary inline-flex items-center gap-1.5 text-xs px-3 py-1.5 disabled:opacity-60 disabled:cursor-not-allowed"
 data-testid="unverified-resend-btn"
 >
 {busy ? <Loader2 size={12} strokeWidth={1.5} className="animate-spin" /> : null}
 {cooldown > 0 ? `Resend (${fmtCooldown(cooldown)})` : "Resend verification email"}
 </button>
 </div>
 </div>
 );
}
